import dummyModels from "../common/data/dummyModelsData";
import a4Logger from "./a4Logger";
import { getJson, postJson, postRaw, putJson, deleteJson } from "./ServiceHelperMethods";


class ModelService {

    // GET /models is server-paged (AI4-207): it answers
    // { items, total, limit, skip }. Omitting limit/skip gets the API default
    // page (20). `workspaceId` filters server-side — filtering the returned
    // page client-side would only ever search within the current page.
    static async getModels({ limit, skip, workspaceId } = {}) {
        const params = new URLSearchParams();
        if (limit != null) params.set('limit', String(limit));
        if (skip != null) params.set('skip', String(skip));
        if (workspaceId) params.set('workspaces', workspaceId);
        const qs = params.toString();
        return await getJson(`models${qs ? `?${qs}` : ''}`);
    }

    static async getModel(modelId) {
        return await getJson(`models/${modelId}`);
    }

    static async putModels(models) {
        return await putJson(`models`, models);
    }

    static async deleteModel(modelId) {
        return await deleteJson(`models/${modelId}`);
    }

    static async downloadModel(model, link) {
        if (link.current.href) { return }

        const modelId = model?._id
        a4Logger.notify("Download started", `Preparing download for "${model?.title}".`)

        const { url } = await getJson(`models/download/${modelId}`);

        const current = link.current
        current.download = `${model?.title}.onnx`
        current.href = url
        current.click()
    }

    static async inferImage(model, imageData) {
        var fd = new FormData();
        fd.append('file', imageData, 'file.jpg');
        return await postRaw(`models/${model._id}/predict`, fd);
    }

    static async classifyZeroShot(imageFile, labels) {
        var fd = new FormData();
        fd.append('file', imageFile, 'file.jpg');
        fd.append('labels', JSON.stringify(labels));
        return await postRaw(`models/zeroshot`, fd);
    }

    static async getTrainStatus(modelId) {
        return await getJson(`models/${modelId}/train/status`);
    }

    static async deployModel(modelId) {
        return await postJson(`models/${modelId}/deploy`);
    }

    static async getDeployStatus(modelId) {
        return await getJson(`models/${modelId}/deploy/status`);
    }

    static async undeployModel(modelId) {
        return await postJson(`models/${modelId}/undeploy`);
    }

    static async bulkDeleteModels(ids) {
        return await postJson(`models/bulk/delete`, { ids });
    }

    static async bulkShareModels(ids, userIds, permission = 'view') {
        return await postJson(`models/bulk/share`, { ids, userIds, permission });
    }

    static async bulkMoveModels(ids, targetWorkspaceId) {
        return await postJson(`models/bulk/move`, { ids, targetWorkspaceId });
    }

    static async getVersions(modelId) {
        return await getJson(`models/${modelId}/versions`);
    }

    static async getVersionDataset(modelId, versionId) {
        return await getJson(`models/${modelId}/versions/${versionId}/dataset`);
    }

    static async getPublicModels({ limit = 20, skip = 0, sort = 'publishedAt' } = {}) {
        const params = new URLSearchParams({ limit, skip, sort });
        return getJson(`public/models?${params.toString()}`);
    }

    static async getPublicModel(slug) {
        return getJson(`public/models/${encodeURIComponent(slug)}`);
    }

    static async getModelMetrics(modelId) {
        return await getJson(`models/${modelId}/metrics`);
    }

    static async batchPredict(modelId, imageFiles) {
        const fd = new FormData();
        imageFiles.forEach((file, i) => fd.append(`file${i}`, file, file.name));
        return await postRaw(`models/${modelId}/batch-predict`, fd);
    }

    static async getInferenceStats(modelId) {
        return await getJson(`models/${modelId}/inference-stats`);
    }

    static async archiveModel(modelId, { reason, successorModelId } = {}) {
        return await postJson(`models/${modelId}/archive`, { reason, successorModelId });
    }

    static async unarchiveModel(modelId) {
        return await postJson(`models/${modelId}/unarchive`, {});
    }
}



export default ModelService;